// Client-side CSV helpers for template downloads and bulk-upload flows.
// Kept dependency-free: ops teams open these files in Excel / Google Sheets.

const escapeCell = (value: string | number | null | undefined): string => {
  const raw = value === null || value === undefined ? '' : String(value);
  // Quote when the cell contains a delimiter, quote or newline; double inner quotes.
  return /[",;\r\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
};

/** Serialize rows to CSV text (CRLF line endings, Excel-compatible). */
export const toCsv = (rows: (string | number | null | undefined)[][]): string =>
  rows.map((row) => row.map(escapeCell).join(',')).join('\r\n');

/** Trigger a browser download of an already-built blob. */
export const downloadBlob = (fileName: string, blob: Blob): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Trigger a browser download of `rows` as a CSV file. A UTF-8 BOM is prepended
 * so Excel on Windows renders non-ASCII characters correctly.
 */
export const downloadCsv = (
  fileName: string,
  rows: (string | number | null | undefined)[][]
): void =>
  downloadBlob(
    fileName,
    new Blob([`﻿${toCsv(rows)}`], { type: 'text/csv;charset=utf-8;' })
  );

/**
 * Parse CSV text into a matrix of trimmed cells. Handles quoted cells (with
 * escaped `""`), CRLF/LF line endings and a UTF-8 BOM. The delimiter is
 * detected from the first line — Excel in some locales writes `;` instead of
 * `,`. Fully empty lines are dropped.
 */
export const parseCsv = (text: string): string[][] => {
  const input = text.replace(/^﻿/, '');
  const firstLine = input.split(/\r?\n/)[0] || '';
  const delimiter =
    (firstLine.match(/;/g)?.length || 0) > (firstLine.match(/,/g)?.length || 0)
      ? ';'
      : ',';

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  const pushCell = () => {
    row.push(cell.trim());
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    if (row.some((value) => value !== '')) {
      rows.push(row);
    }
    row = [];
  };

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      pushCell();
    } else if (char === '\n') {
      pushRow();
    } else if (char !== '\r') {
      cell += char;
    }
  }

  // Trailing cell / row (file without a final newline).
  if (cell !== '' || row.length > 0) {
    pushRow();
  }

  return rows;
};
